import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import Header from "./Header";

const API_URL = import.meta.env.VITE_WORKER_URL || "";

// Authentication service
const authService = {
  getCurrentUser: () => {
    const userStr = localStorage.getItem("user");
    if (userStr) return JSON.parse(userStr);
    return null;
  },
  getToken: () => localStorage.getItem("auth_token"),
  logout: () => {
    localStorage.removeItem("user");
    localStorage.removeItem("auth_token");
  },
};

export default function DashboardLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let timeoutId;

    const verifyUser = async () => {
      const currentUser = authService.getCurrentUser();
      const token = authService.getToken();

      if (!currentUser || !token) {
        navigate("/login");
        return;
      }

      try {
        console.log("Verifying token with:", `${API_URL}/api/verify`);
        const response = await fetch(`${API_URL}/api/verify`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = await response.json().catch(() => ({}));

        // ✅ Consider valid if 200 or explicitly says valid/success
        if (response.ok && (data.valid === true || data.success === true || !data.valid)) {
          setUser(currentUser);

          // ⏳ Set session timeout (1 hour)
          timeoutId = setTimeout(() => {
            console.warn("Session expired. Logging out...");
            authService.logout();
            navigate("/login");
          }, 60 * 60 * 1000); // 1 hour
        } else {
          throw new Error("Invalid token");
        }
      } catch (error) {
        console.error("Token verification failed:", error);
        authService.logout();
        navigate("/login");
      } finally {
        setLoading(false);
      }
    };

    verifyUser();

    // 🧹 Cleanup: clear timeout when component unmounts
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [navigate]);

  const handleLogout = () => {
    authService.logout();
    navigate("/login");
  };

  const toggleSidebarVisibility = () => {
    setSidebarVisible(!sidebarVisible);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900">
      {sidebarVisible && (
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          user={user}
          onLogout={handleLogout}
        />
      )}

      <div className="flex-1 flex flex-col transition-all duration-300">
        <Header
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          onToggleSidebarVisibility={toggleSidebarVisibility}
          sidebarVisible={sidebarVisible}
          user={user}
          onLogout={handleLogout}
        />

        <main className="flex-1 p-4 md:p-6 overflow-auto">{children}</main>
      </div>
    </div>
  );
}
